import { useEffect, useState, useRef, useCallback } from "react";
import apiSubscription from "../../services/subscription";

/**
 * Guest token lifecycle for the subscription flow.
 *
 * Why guest tokens exist:
 *  Some endpoints (validate codes, fetch project metadata) need authenticated
 *  but the user hasn't created an account yet. The backend issues a short-lived
 *  "guest token" tied to the project, scoped to read-only reference data.
 *
 * Lifecycle:
 *  1. On mount: request a token, store in sessionStorage so axios picks it up
 *  2. While alive: refresh ~5 min before expiry (auto-renew)
 *  3. On unmount: do NOT clear — sessionStorage is per-tab and tab close
 *     handles cleanup naturally
 *
 * Mock mode:
 *  In USE_MOCK_DATA, we just stuff a fake token into sessionStorage so other
 *  hooks don't bail out on `!sessionStorage.guest_token` checks.
 *
 * Returns:
 *   { isReady, isExpired, error }
 *   isReady   = token has been obtained (real or mock); other hooks can fetch
 *   isExpired = token expired and refresh failed; show "session expired" UI
 *   error     = last error from token endpoints (for debug)
 */
const USE_MOCK_DATA = true;
const TOKEN_TTL_SECONDS = 60 * 60;        // 1 hour — backend may override
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60_000; // refresh 5 min before expiry

export function useGuestToken({ id_project } = {}) {
  const [isReady, setIsReady] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [error, setError] = useState(null);

  // Hold the refresh timer outside React state so we can cancel it on unmount
  // without triggering re-renders.
  const refreshTimer = useRef(null);

  const requestToken = useCallback(async () => {
    if (!id_project) return;

    try {
      if (USE_MOCK_DATA) {
        sessionStorage.setItem("guest_token", "mock_guest_token");
        setIsReady(true);
        return;
      }

      const res = await apiSubscription.createGuestToken({ id_project });
      const token = res?.dataRes?.token ?? res?.token;
      if (!token) throw new Error("Token missing in response");

      sessionStorage.setItem("guest_token", token);
      setIsReady(true);
      setIsExpired(false);
      setError(null);

      // Schedule refresh
      const ttlMs = (res?.dataRes?.expires_in ?? TOKEN_TTL_SECONDS) * 1000;
      const refreshIn = Math.max(0, ttlMs - REFRESH_BEFORE_EXPIRY_MS);
      refreshTimer.current = setTimeout(requestToken, refreshIn);
    } catch (err) {
      setError(err);
      setIsExpired(true);
      setIsReady(false);
    }
  }, [id_project]);

  useEffect(() => {
    requestToken();
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [requestToken]);

  return { isReady, isExpired, error };
}